import { useCallback } from 'react';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl';

export interface UseFileDropZoneErrorMessages {
  fileTooLarge?: string;
  fileInvalidType?: string;
}

export interface UseFileDropZoneOptions {
  accept: Accept;
  maxSize: number;
  onDrop?: (file: File) => void;
  onError?: (message: string) => void;
  errorMessages?: UseFileDropZoneErrorMessages;
}

export interface UseFileDropZoneReturn {
  getRootProps: ReturnType<typeof useDropzone>['getRootProps'];
  getInputProps: ReturnType<typeof useDropzone>['getInputProps'];
  isDragActive: boolean;
  hasError?: boolean;
  file?: File;
}

export const useFileDropZone = ({
  accept,
  maxSize,
  onDrop,
  onError,
  errorMessages,
}: UseFileDropZoneOptions): UseFileDropZoneReturn => {
  const { formatMessage } = useIntl();

  const getErrorMessage = useCallback(
    (rejection: FileRejection) => {
      const error = rejection.errors[0];
      if (!error) return;

      if (error.code === 'file-too-large') {
        return errorMessages?.fileTooLarge ?? formatMessage({ id: 'dnd-Error-FileIsTooLarge' });
      }
      if (error.code === 'file-invalid-type') {
        return (
          errorMessages?.fileInvalidType ?? formatMessage({ id: 'dnd-Error-FileTypeNotSupported' })
        );
      }
      return error.message;
    },
    [errorMessages, formatMessage],
  );

  const handleDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      const rejection = rejections[0];
      if (rejection) {
        const message = getErrorMessage(rejection);
        if (message) onError?.(message);
      }
      if (accepted[0]) {
        onDrop?.(accepted[0]);
      }
    },
    [onDrop, onError, getErrorMessage],
  );

  const { getRootProps, getInputProps, isDragActive, acceptedFiles, fileRejections } = useDropzone({
    onDrop: handleDrop,
    maxSize,
    maxFiles: 1,
    multiple: false,
    accept,
  });

  return {
    getRootProps,
    getInputProps,
    isDragActive,
    hasError: fileRejections.length > 0,
    file: acceptedFiles[0],
  };
};
